import React, { useState, useEffect, useMemo } from 'react';
import { formatEuro, formatPercent, formatNumber } from '../../utils/currency';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Paper,
  LinearProgress,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import PieChartIcon from '@mui/icons-material/PieChart';
import BalanceIcon from '@mui/icons-material/Balance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RecommendIcon from '@mui/icons-material/Recommend';
import { getStocks, StockDto } from '../../services/stocksApi';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

interface AllocationData {
  name: string;
  value: number;
  percentage: number;
  target?: number;
  color: string;
}

interface RebalancingRecommendation {
  symbol: string;
  currentAllocation: number;
  targetAllocation: number;
  difference: number;
  action: 'BUY' | 'SELL' | 'HOLD';
  amount: number;
  shares?: number;
}

interface SectorAllocation {
  sector: string;
  currentValue: number;
  currentPercentage: number;
  targetPercentage: number;
  difference: number;
  stocks: string[];
}

const COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#00796b', '#c2185b', '#5d4037'];

const SECTOR_TARGETS = {
  'Technology': 25,
  'Healthcare': 15,
  'Financial Services': 20,
  'Consumer Cyclical': 10,
  'Industrials': 10,
  'Energy': 5,
  'Real Estate': 5,
  'Utilities': 5,
  'Materials': 3,
  'Other': 2,
};

// Display names for the sector keys (the keys stay in English: they match the targets map).
const SECTOR_LABELS: { [key: string]: string } = {
  'Technology': 'Tecnologia',
  'Healthcare': 'Saúde',
  'Financial Services': 'Serviços financeiros',
  'Consumer Cyclical': 'Consumo cíclico',
  'Consumer Defensive': 'Consumo defensivo',
  'Industrials': 'Indústria',
  'Energy': 'Energia',
  'Real Estate': 'Imobiliário',
  'Utilities': 'Serviços públicos',
  'Materials': 'Materiais',
  'Other': 'Outros',
};
const sectorLabel = (sector: string): string => SECTOR_LABELS[sector] || sector;

const AllocationPage: React.FC = () => {
  const [stocks, setStocks] = useState<StockDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rebalanceMode, setRebalanceMode] = useState<'automatic' | 'manual'>('automatic');
  const [customTargets, setCustomTargets] = useState<{[key: string]: number}>(SECTOR_TARGETS);

  useEffect(() => {
    loadAllocationData();
  }, []);

  const loadAllocationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allStocks = await getStocks();
      setStocks(allStocks);
      
    } catch (err) {
      console.error('Error loading allocation data:', err);
      setError('Não foi possível carregar os dados de alocação');
    } finally {
      setLoading(false);
    }
  };

  // Mock function to get sector for a stock
  const getSectorForStock = (symbol: string): string => {
    const sectorMapping: {[key: string]: string} = {
      'AAPL': 'Technology',
      'MSFT': 'Technology',
      'GOOGL': 'Technology',
      'AMZN': 'Consumer Cyclical',
      'TSLA': 'Consumer Cyclical',
      'JNJ': 'Healthcare',
      'PFE': 'Healthcare',
      'JPM': 'Financial Services',
      'BAC': 'Financial Services',
      'XOM': 'Energy',
      'CVX': 'Energy',
      'BRK.B': 'Financial Services',
      'UNH': 'Healthcare',
      'HD': 'Consumer Cyclical',
      'PG': 'Consumer Defensive',
    };
    
    return sectorMapping[symbol] || 'Other';
  };

  // Calculate current allocation by stock
  const stockAllocation = useMemo((): AllocationData[] => {
    const totalValue = stocks.reduce((sum, stock) => {
      const shares = stock.purchases.reduce((shareSum, purchase) => shareSum + purchase.quantity, 0);
      return sum + (shares * (stock.currentPrice || 0));
    }, 0);

    return stocks.map((stock, index) => {
      const shares = stock.purchases.reduce((shareSum, purchase) => shareSum + purchase.quantity, 0);
      const value = shares * (stock.currentPrice || 0);
      const percentage = totalValue > 0 ? (value / totalValue) * 100 : 0;

      return {
        name: stock.symbol,
        value,
        percentage,
        color: COLORS[index % COLORS.length],
      };
    }).sort((a, b) => b.percentage - a.percentage);
  }, [stocks]);

  // Calculate sector allocation
  const sectorAllocation = useMemo((): SectorAllocation[] => {
    const totalValue = stockAllocation.reduce((sum, stock) => sum + stock.value, 0);
    const sectorMap = new Map<string, { value: number; stocks: string[] }>();

    // Group stocks by sector (mock data - in real app, get from stock data)
    stocks.forEach(stock => {
      const sector = getSectorForStock(stock.symbol); // Mock function
      const shares = stock.purchases.reduce((shareSum, purchase) => shareSum + purchase.quantity, 0);
      const value = shares * (stock.currentPrice || 0);

      if (sectorMap.has(sector)) {
        const existing = sectorMap.get(sector)!;
        existing.value += value;
        existing.stocks.push(stock.symbol);
      } else {
        sectorMap.set(sector, { value, stocks: [stock.symbol] });
      }
    });

    return Array.from(sectorMap.entries()).map(([sector, data]) => ({
      sector,
      currentValue: data.value,
      currentPercentage: totalValue > 0 ? (data.value / totalValue) * 100 : 0,
      targetPercentage: customTargets[sector] || 0,
      difference: (totalValue > 0 ? (data.value / totalValue) * 100 : 0) - (customTargets[sector] || 0),
      stocks: data.stocks,
    })).sort((a, b) => b.currentPercentage - a.currentPercentage);
  }, [stocks, stockAllocation, customTargets]);

  // Generate rebalancing recommendations
  const rebalancingRecommendations = useMemo((): RebalancingRecommendation[] => {
    const totalValue = stockAllocation.reduce((sum, stock) => sum + stock.value, 0);
    const recommendations: RebalancingRecommendation[] = [];

    sectorAllocation.forEach(sector => {
      if (Math.abs(sector.difference) > 2) { // Only recommend if difference > 2%
        const targetValue = (sector.targetPercentage / 100) * totalValue;
        const amountDifference = targetValue - sector.currentValue;

        // For simplicity, distribute the adjustment across stocks in the sector
        sector.stocks.forEach(symbol => {
          const stock = stocks.find(s => s.symbol === symbol);
          if (stock) {
            const currentShares = stock.purchases.reduce((sum, purchase) => sum + purchase.quantity, 0);
            const currentValue = currentShares * (stock.currentPrice || 0);
            const currentAllocation = totalValue > 0 ? (currentValue / totalValue) * 100 : 0;
            
            // Distribute the sector adjustment proportionally
            const stockAdjustment = (currentValue / sector.currentValue) * amountDifference;
            const targetAllocation = currentAllocation + ((stockAdjustment / totalValue) * 100);
            
            if (Math.abs(stockAdjustment) > 100) { // Only recommend if adjustment > €100
              recommendations.push({
                symbol,
                currentAllocation,
                targetAllocation,
                difference: targetAllocation - currentAllocation,
                action: stockAdjustment > 0 ? 'BUY' : 'SELL',
                amount: Math.abs(stockAdjustment),
                shares: Math.round(Math.abs(stockAdjustment) / (stock.currentPrice || 1)),
              });
            }
          }
        });
      }
    });

    return recommendations.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  }, [stocks, sectorAllocation, stockAllocation]);

  const formatCurrency = (amount: number): string => formatEuro(amount);
  const formatPercentage = (percentage: number): string => formatPercent(percentage, 1);

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Alocação da carteira"
        subtitle="Gira a alocação da carteira e a estratégia de reequilíbrio"
        actions={
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Reequilíbrio</InputLabel>
            <Select
              value={rebalanceMode}
              label="Reequilíbrio"
              onChange={(e) => setRebalanceMode(e.target.value as any)}
            >
              <MenuItem value="automatic">Automático</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
            </Select>
          </FormControl>
        }
      />

      {/* Allocation Summary */}
      <AutoGrid min={240} gap={3} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PieChartIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total de posições
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {stockAllocation.length} ações
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BalanceIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Maior posição
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : stockAllocation.length > 0 ? (
              <Box>
                <Typography variant="h6" component="div">
                  {stockAllocation[0].name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatPercentage(stockAllocation[0].percentage)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sem posições
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <RecommendIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Ações de reequilíbrio
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {rebalancingRecommendations.length}
              </Typography>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Allocation Charts */}
      <AutoGrid min={400} gap={3} sx={{ mb: 3 }}>
        {/* Stock Allocation Pie Chart */}
        <Paper sx={{ p: 3, minHeight: 400 }}>
          <Typography variant="h6" gutterBottom>
            Alocação por ação
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : stockAllocation.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <Typography variant="body2" color="text.secondary">
                Sem ações para mostrar
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stockAllocation}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} ${formatNumber(percentage, 1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stockAllocation.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>

        {/* Sector Allocation Bar Chart */}
        <Paper sx={{ p: 3, minHeight: 400 }}>
          <Typography variant="h6" gutterBottom>
            Alocação por setor vs objetivo
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : sectorAllocation.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
              <Typography variant="body2" color="text.secondary">
                Sem dados de setores para mostrar
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sectorAllocation} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="sector" type="category" width={100} tickFormatter={sectorLabel} />
                <Tooltip formatter={(value) => `${formatNumber(Number(value), 1)}%`} labelFormatter={(label) => sectorLabel(String(label))} />
                <Legend />
                <Bar dataKey="currentPercentage" fill="#1976d2" name="Atual %" />
                <Bar dataKey="targetPercentage" fill="#4caf50" name="Objetivo %" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </AutoGrid>

      {/* Sector Allocation Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Detalhe da alocação por setor
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Setor</TableCell>
                <TableCell align="right">Valor atual</TableCell>
                <TableCell align="center">Atual %</TableCell>
                <TableCell align="center">Objetivo %</TableCell>
                <TableCell align="center">Diferença</TableCell>
                <TableCell align="right">Progresso</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : sectorAllocation.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Sem dados de alocação por setor
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sectorAllocation.map((sector) => (
                  <TableRow key={sector.sector} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {sectorLabel(sector.sector)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(sector.currentValue)}
                    </TableCell>
                    <TableCell align="center">
                      {formatPercentage(sector.currentPercentage)}
                    </TableCell>
                    <TableCell align="center">
                      {formatPercentage(sector.targetPercentage)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        color={Math.abs(sector.difference) <= 2 ? 'success.main' : 'warning.main'}
                        fontWeight="bold"
                      >
                        {formatPercentage(sector.difference)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((sector.currentPercentage / sector.targetPercentage) * 100, 100)}
                          sx={{ flexGrow: 1, mr: 1 }}
                          color={Math.abs(sector.difference) <= 2 ? 'success' : 'warning'}
                        />
                        <Typography variant="caption" sx={{ minWidth: 35 }}>
                          {Math.round((sector.currentPercentage / sector.targetPercentage) * 100)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sector.stocks.map(stock => (
                          <Chip key={stock} label={stock} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Rebalancing Recommendations */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Recomendações de reequilíbrio
          </Typography>
          <Button
            variant="contained"
            startIcon={<TrendingUpIcon />}
            disabled={rebalancingRecommendations.length === 0}
          >
            Executar reequilíbrio
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Símbolo</TableCell>
                <TableCell align="center">Ação</TableCell>
                <TableCell align="right">Atual %</TableCell>
                <TableCell align="right">Objetivo %</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="right">Unidades</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from({ length: 6 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rebalancingRecommendations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      A sua carteira está equilibrada. Não é necessário reequilibrar.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rebalancingRecommendations.map((recommendation) => (
                  <TableRow key={recommendation.symbol} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {recommendation.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={recommendation.action === 'BUY' ? 'COMPRAR' : 'VENDER'}
                        size="small"
                        color={recommendation.action === 'BUY' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatPercentage(recommendation.currentAllocation)}
                    </TableCell>
                    <TableCell align="right">
                      {formatPercentage(recommendation.targetAllocation)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        color={recommendation.action === 'BUY' ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {formatCurrency(recommendation.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {recommendation.shares || 0} unidades
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AllocationPage;
